// Where the section fly-out sits.
//
// It renders through a portal, so it has no layout relationship to the row it
// belongs to and has to be placed in viewport coordinates instead — beside the
// row, clamped so a long section cannot run off the bottom of the screen.

import { useLayoutEffect, useState, type RefObject } from 'react';

/** Gap between the nav and the fly-out. */
const GAP = 8;
/** Margin it keeps from the viewport edges. */
const EDGE = 12;

export interface FlyoutPosition {
  left: number;
  top: number;
}

export function useFlyoutPosition(
  anchor: HTMLElement,
  surfaceRef: RefObject<HTMLElement | null>,
  /** Row count — the height it clamps against changes with it. */
  rows: number,
  onClose: () => void,
): FlyoutPosition | null {
  const [pos, setPos] = useState<FlyoutPosition | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const height = surfaceRef.current?.offsetHeight ?? 0;
      const maxTop = Math.max(EDGE, window.innerHeight - height - EDGE);
      const left = a.right + GAP;
      const top = Math.min(Math.max(a.top, EDGE), maxTop);
      // The scroll listener is capture-phase, so it hears every scrolling
      // element on the page. Most of those do not move the anchor at all, and
      // a fresh object each time would re-render the fly-out for the whole
      // gesture — so only commit a position that actually changed.
      setPos((prev) => (prev && prev.left === left && prev.top === top ? prev : { left, top }));
    };
    place();
    // Scrolling moves the row, so follow it. A resize can take the row away
    // entirely — the panel's two bands swap at `lg` — and a fly-out anchored to
    // a row that is no longer on screen has nowhere honest to sit, so close
    // rather than chase it. Wrapped, not passed straight in: the listener's
    // Event argument would land in `restoreFocus` and drag focus to the rail.
    const closeOnResize = () => onClose();
    window.addEventListener('resize', closeOnResize);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', closeOnResize);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, rows, onClose, surfaceRef]);

  return pos;
}
